import {
    MenuActions,
    filterOptions,
    getActionFromKey,
    getUpdatedIndex,
    isScrollable,
    maintainScrollVisibility
} from './combobox-utils.js';

/*
 * ARIA 1.2 Filtered Combobox code
 */
const ComboboxFilter = function (el, options) {
    // element refs
    this.el = el;
    this.inputEl = el.querySelector('input');
    this.valueInputEl = el.querySelector('[js-combobox-post-value]');
    this.listboxEl = el.querySelector('[role=listbox]');

    // data
    this.idBase = this.inputEl.id;
    this.options = options;
    this.filteredOptions = options;

    // state
    this.hasInitValue = !!this.valueInputEl.value;
    this.activeId = this.hasInitValue ? this.valueInputEl.value : [...this.options.keys()][0];
    this.open = false;
}

ComboboxFilter.prototype.init = function () {
    this.inputEl.addEventListener('input', this.onInput.bind(this));
    this.inputEl.addEventListener('blur', this.onInputBlur.bind(this));
    this.inputEl.addEventListener('click', () => this.updateMenuState(true));
    this.inputEl.addEventListener('keydown', this.onInputKeyDown.bind(this));
    
    if (this.hasInitValue) {
        this.onOptionChange(this.activeId);
        this.selectOption(this.activeId);
    }

    [...this.options].map(([optionId, optionLabel], index) => {
        const optionEl = document.createElement('div');
        optionEl.setAttribute('role', 'option');
        optionEl.id = optionId;
        optionEl.className =
            (this.hasInitValue && this.activeId === optionEl.id)
                ? 'combo-option option-current'
                : (!this.hasInitValue && index === 0)
                    ? 'combo-option option-current'
                    : 'combo-option';

        optionEl.setAttribute('aria-selected', (this.hasInitValue && this.activeId === optionEl.id) ? 'true' : 'false');
        optionEl.innerText = optionLabel;

        optionEl.addEventListener('click', () => { this.onOptionClick(optionId); });
        optionEl.addEventListener('mousedown', this.onOptionMouseDown.bind(this));

        this.listboxEl.appendChild(optionEl);
    });
}

ComboboxFilter.prototype.filterOptions = function (value) {
    this.filteredOptions = filterOptions(this.options, value);

    // hide/show options based on filtering
    const options = this.el.querySelectorAll('[role=option]');
    [...options].forEach((optionEl) =>
        (this.filteredOptions.has(optionEl.id))
            ? optionEl.style.display = 'block'
            : optionEl.style.display = 'none'
    );
}

ComboboxFilter.prototype.onInput = function () {
    const curValue = this.inputEl.value;
    this.filterOptions(curValue);

    // if active option is not in filtered options, set it to first filtered option
    if (!this.filteredOptions.has(this.activeId) && this.filteredOptions.size > 0) {
        const [firstFilteredId] = this.filteredOptions.entries().next().value
        this.onOptionChange(firstFilteredId);
    }

    const menuState = this.filteredOptions.size > 0;
    if (this.open !== menuState) {
        this.updateMenuState(menuState, false);
    }
}

ComboboxFilter.prototype.onInputKeyDown = function (event) {
    const action = getActionFromKey(event, this.open);

    switch (action) {
        case MenuActions.Next:
        case MenuActions.Last:
        case MenuActions.First:
        case MenuActions.Previous:
            event.preventDefault();
            const filteredOptionsArray = [...this.filteredOptions];
            const max = filteredOptionsArray.length - 1;
            const nextFilteredIndex = getUpdatedIndex(
                filteredOptionsArray.findIndex(([id]) => id === this.activeId),
                max,
                action
            );
            const [id] = filteredOptionsArray[nextFilteredIndex];

            return this.onOptionChange(id);
        case MenuActions.CloseSelect:
            event.preventDefault();
            this.selectOption(this.activeId);
            return this.updateMenuState(false);
        case MenuActions.Close:
            event.preventDefault();
            return this.updateMenuState(false);
        case MenuActions.Open:
            return this.updateMenuState(true);
    }
}

ComboboxFilter.prototype.onInputBlur = function () {
    if (this.ignoreBlur) {
        this.ignoreBlur = false;
        return;
    }
    if (this.open) {
        this.updateMenuState(false, false);
    }
}

ComboboxFilter.prototype.onOptionChange = function (id) {
    this.activeId = id;
    this.valueInputEl.value = id;
    this.inputEl.setAttribute('aria-activedescendant', id);

    // update active style
    const options = this.el.querySelectorAll('[role=option]');

    [...options].forEach((optionEl) => {
        if (optionEl.id === id) {
            // todo dubbelkolla så att allt tas bort och läggs till rätt
            optionEl.classList.add('option-current');

            if (this.open && isScrollable(this.listboxEl)) {
                maintainScrollVisibility(optionEl, this.listboxEl);
            }
            return;
        }
        return optionEl.classList.remove('option-current');
    });
}

ComboboxFilter.prototype.onOptionClick = function (id) {
    this.onOptionChange(id);
    this.selectOption(id);
    this.updateMenuState(false);
}

ComboboxFilter.prototype.onOptionMouseDown = function () {
    this.ignoreBlur = true;
}

ComboboxFilter.prototype.selectOption = function (id) {
    const selected = this.options.get(id);
    this.valueInputEl.value = id;
    this.inputEl.value = selected;

    // update aria-selected
    const options = this.el.querySelectorAll('[role=option]');
    [...options].forEach((optionEl) => {
        optionEl.setAttribute('aria-selected', 'false');

        if (optionEl.id === id) {
            optionEl.setAttribute('aria-selected', 'true');
        }
    });
}

ComboboxFilter.prototype.updateMenuState = function (open, callFocus = true) {
    this.open = open;

    this.inputEl.setAttribute('aria-expanded', `${open}`);
    open ? this.el.classList.add('open') : this.el.classList.remove('open');
    callFocus && this.inputEl.focus();
}

export default ComboboxFilter;

/* example usage 
    // replace [id] with the id of the combobox, ie competence in "Kontaka oss" filter input
    <label for="[id]--input" class="input__label">Combobox (with filtering)</label>
    <div class="combo" js-combobox-filter>
        <input
            id="[id]--input"
            class="combo-input"
            aria-activedescendant=""
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-expanded="false"
            aria-controls="[id]--listbox"
            role="combobox" 
            type="text"
            placeholder="Välj en placeholder"    
        >   
        <div class="combo-menu" role="listbox" id="[id]--listbox"></div>
        <input hidden id="[id]" name="[id]" value="[serverside rendered value]" js-combobox-post-value />
        <datalist js-combobox-data hidden>
            // js fetches data from here and populates the div.combo-menu.
            @foreach (var val in Model.DataSet)
            {
                <option id="@val.Value" value="@val.Value">@val.Text</option>
            }
        </datalist>
    </div>
*/
